// Environments — lista e Edit Environment (docs/screens/14-environments.md).
// Dono do dado: arquivos JSON versionados em `agentes/` no repo by-mav/hope,
// via API de contents do GitHub. Esta tela só lê e grava a config; o
// enforcement de rede (Unrestricted/Limited, allowed hosts) é do runner.

import { gateway, github, SourceError } from "../api";
import { empty, errorBox, esc, head, skeleton, table, toast } from "../ui";

const REPO = "by-mav/hope";
const DIR = "agentes";

const CSS = `
  .environments-form { max-width: 620px; }
  .environments-section { margin: 18px 0 6px; font-weight: 600; }
  .environments-switch { display: inline-flex; align-items: center; gap: 10px; cursor: pointer; margin: 8px 0; }
  .environments-switch input { position: absolute; opacity: 0; }
  .environments-track { width: 36px; height: 20px; border-radius: 10px; background: rgba(255,255,255,.18); position: relative; transition: background .15s; flex: none; }
  .environments-track::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: left .15s; }
  .environments-switch input:checked + .environments-track { background: var(--accent, #e8c34c); }
  .environments-switch input:checked + .environments-track::after { left: 18px; }
  .environments-radio { display: flex; gap: 18px; margin: 8px 0 12px; }
  .environments-radio label { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
  .environments-subpanel { border: 1px solid rgba(255,255,255,.12); border-radius: 8px; padding: 14px; margin-top: 10px; }
  .environments-subpanel .environments-switch { display: flex; }
`;
if (!document.getElementById("css-environments")) {
  const st = document.createElement("style");
  st.id = "css-environments";
  st.textContent = CSS;
  document.head.append(st);
}

// ------------------------------------------------------------------- formas

interface Pacotes {
  pip?: string;
  npm?: string;
}

interface Rede {
  mode?: string;
  allowed_hosts?: string[];
  allow_mcp_servers?: boolean;
  allow_package_managers?: boolean;
}

/** O JSON do environment. Só os campos do formulário são tipados; o resto do
 *  arquivo (imagem, isolamento, papéis…) é preservado intacto no Save. */
type Env = Record<string, unknown> & {
  name?: string;
  available_in_all_projects?: boolean;
  packages?: Pacotes;
  networking?: Rede;
};

// ---------------------------------------------------------------- utilidades

/** Base64 da API de contents, com UTF-8 correto — `atob` sozinho quebra acento. */
const deB64 = (s: string): string =>
  new TextDecoder().decode(Uint8Array.from(atob(s.replace(/\s/g, "")), (c) => c.charCodeAt(0)));

const paraB64 = (s: string): string =>
  btoa(Array.from(new TextEncoder().encode(s), (b) => String.fromCharCode(b)).join(""));

/** Mesmo envelope das páginas em pages.ts: esqueleto, corpo async, erro tratado. */
async function load(el: HTMLElement, header: string, body: () => Promise<string>): Promise<void> {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

// ------------------------------------------------------------------- página

/** Rotas: #/ambientes (lista) e #/ambientes/editar[/{env}|/novo] (formulário). */
export const environments = async (el: HTMLElement): Promise<void> => {
  const partes = location.hash.replace(/^#/, "").split("/");
  if (partes[2] === "editar") return editar(el, partes[3] ?? "agente");
  return lista(el);
};

// Rótulo do botão de criar é INFERIDO no spec (cortado no frame); segue o
// padrão "+ New …" das outras páginas.
const CRIAR = `<a class="btn btn-primary" href="#/ambientes/editar/novo">+ New Environment</a>`;

async function lista(el: HTMLElement): Promise<void> {
  const header = head({ title: "Environments", actions: CRIAR });

  await load(el, header, async () => {
    const entradas = (await github.contents(REPO, DIR)).filter(
      (e) => e.type === "file" && e.name.endsWith(".json"),
    );
    if (!entradas.length) {
      return empty(
        "Nenhum environment",
        "O environment define como um agente executa: pacotes, rede e permissões.",
        CRIAR,
      );
    }
    const linhas = entradas.map((e) => {
      const stem = e.name.replace(/\.json$/, "");
      return `<tr>
        <td><a href="#/ambientes/editar/${encodeURIComponent(stem)}">${esc(stem)}</a></td>
        <td>${e.size} B</td>
      </tr>`;
    });
    return table(["Name", "Size"], linhas);
  });
}

// -------------------------------------------------------------- formulário

async function editar(el: HTMLElement, stem: string): Promise<void> {
  const novo = stem === "novo";

  // Header próprio: `←` fica à esquerda do título, coisa que head() não faz.
  const header = `
    <header class="page-head">
      <div style="display:flex;align-items:center;gap:12px">
        <a class="btn" href="#/ambientes" aria-label="Voltar">←</a>
        <h1 class="page-title">Edit Environment</h1>
      </div>
      <div class="page-actions">
        <a class="btn" href="#/ambientes">Cancel</a>
        <button class="btn btn-primary" type="submit" form="environments-form">Save</button>
      </div>
    </header>`;

  // Declarados fora do corpo async: `liga()` só pode rodar depois que `load()`
  // escreveu o formulário no DOM, e precisa do que a carga leu.
  let env: Env = {};
  let sha: string | undefined;

  await load(el, header, async () => {
    if (!novo) {
      const arq = await gateway<{ content: string; sha: string }>(
        `github/repos/${REPO}/contents/${DIR}/${encodeURIComponent(stem)}.json`,
      );
      sha = arq.sha;
      env = JSON.parse(deB64(arq.content)) as Env;
    }

    const net: Rede = env.networking ?? {};
    const limitado = (net.mode ?? "limited") === "limited";

    return `
      <div class="card environments-form">
        <div class="card-body">
          <form id="environments-form">
            <div class="field">
              <label class="label" for="env-name">Name</label>
              <input class="input" id="env-name" name="name" value="${esc(env.name ?? (novo ? "" : stem))}" />
            </div>
            <label class="environments-switch">
              <input type="checkbox" name="all_projects" ${env.available_in_all_projects ? "checked" : ""} />
              <span class="environments-track"></span>
              Available in all projects
            </label>
            <div class="environments-section">Packages</div>
            <div class="field">
              <label class="label" for="env-pip">pip</label>
              <input class="input" id="env-pip" name="pip" placeholder="pandas, numpy, requests" value="${esc(env.packages?.pip ?? "")}" />
            </div>
            <div class="field">
              <label class="label" for="env-npm">npm</label>
              <input class="input" id="env-npm" name="npm" placeholder="cheerio, lodash" value="${esc(env.packages?.npm ?? "")}" />
            </div>
            <div class="environments-section">Networking</div>
            <div class="environments-radio">
              <label><input type="radio" name="mode" value="unrestricted" ${limitado ? "" : "checked"} /> Unrestricted</label>
              <label><input type="radio" name="mode" value="limited" ${limitado ? "checked" : ""} /> Limited</label>
            </div>
            <div class="environments-subpanel" id="environments-subpanel" ${limitado ? "" : "hidden"}>
              <div class="field">
                <label class="label" for="env-hosts">Allowed hosts (one per line)</label>
                <textarea class="textarea" id="env-hosts" name="hosts" rows="4">${esc((net.allowed_hosts ?? []).join("\n"))}</textarea>
              </div>
              <label class="environments-switch">
                <input type="checkbox" name="mcp" ${net.allow_mcp_servers || novo ? "checked" : ""} />
                <span class="environments-track"></span>
                Allow MCP servers
              </label>
              <label class="environments-switch">
                <input type="checkbox" name="pkg" ${net.allow_package_managers || novo ? "checked" : ""} />
                <span class="environments-track"></span>
                Allow package managers
              </label>
            </div>
          </form>
        </div>
      </div>`;
  });

  // Se a carga falhou, `load` renderiu errorBox e `liga` sai no `if (!form)`.
  liga(el, env, sha, novo ? null : stem);
}

function liga(el: HTMLElement, env: Env, sha: string | undefined, stemAtual: string | null): void {
  const form = el.querySelector<HTMLFormElement>("#environments-form");
  if (!form) return;

  // Sub-painel de allowed hosts: visibilidade DERIVADA do radio, local.
  const sub = el.querySelector<HTMLElement>("#environments-subpanel")!;
  form.addEventListener("change", () => {
    sub.hidden = new FormData(form).get("mode") !== "limited";
  });

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void salvar(el, form, env, sha, stemAtual);
  });
}

async function salvar(
  el: HTMLElement,
  form: HTMLFormElement,
  env: Env,
  sha: string | undefined,
  stemAtual: string | null,
): Promise<void> {
  const d = new FormData(form);
  const name = String(d.get("name") ?? "").trim();
  if (!name) return toast("Todo environment precisa de nome");

  // Um único PUT com o JSON inteiro: campos do formulário por cima do que já
  // existia no arquivo — nada do que a tela não mostra é perdido.
  const corpo: Env = {
    ...env,
    name,
    available_in_all_projects: d.get("all_projects") === "on",
    packages: {
      ...(env.packages ?? {}),
      pip: String(d.get("pip") ?? "").trim(),
      npm: String(d.get("npm") ?? "").trim(),
    },
    networking: {
      ...(env.networking ?? {}),
      mode: String(d.get("mode") ?? "limited"),
      allowed_hosts: String(d.get("hosts") ?? "")
        .split(/\r?\n/)
        .map((h) => h.trim())
        .filter(Boolean),
      allow_mcp_servers: d.get("mcp") === "on",
      allow_package_managers: d.get("pkg") === "on",
    },
  };

  // Renomear muda o path (contrato): PUT no path novo + delete do antigo.
  // Só é rename quando o arquivo já tinha `name` e ele mudou — arquivo antigo
  // sem `name` ganha o campo no lugar, sem mover (senão o primeiro Save de
  // agente.json o moveria de endereço e quebraria quem lê o path fixo).
  const renomeado = stemAtual !== null && typeof env.name === "string" && env.name !== name;
  const stemNovo = renomeado || stemAtual === null ? name : stemAtual;

  const botao = el.querySelector<HTMLButtonElement>('button[form="environments-form"]')!;
  botao.disabled = true;
  try {
    await gateway(`github/repos/${REPO}/contents/${DIR}/${encodeURIComponent(stemNovo)}.json`, {
      method: "PUT",
      body: JSON.stringify({
        message: `environments: salva ${DIR}/${stemNovo}.json`,
        content: paraB64(JSON.stringify(corpo, null, 2) + "\n"),
        ...(renomeado || !sha ? {} : { sha }),
      }),
    });
    if (renomeado && sha && stemAtual) {
      await gateway(`github/repos/${REPO}/contents/${DIR}/${encodeURIComponent(stemAtual)}.json`, {
        method: "DELETE",
        body: JSON.stringify({
          message: `environments: renomeia ${stemAtual} → ${stemNovo}`,
          sha,
        }),
      });
    }
    toast(`Environment salvo em ${DIR}/${stemNovo}.json`);
    location.hash = "/ambientes";
  } catch (err) {
    toast((err as SourceError).message);
    botao.disabled = false;
  }
}
